import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Input,
  List,
  message,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { PERMISSIONS } from "../../constants/permissions";
import { usePermissions } from "../../hooks/usePermissions";
import { appInfoService } from "../../services/AppInfoService";
import { ApiResponse, OperationStatus } from "../../types/api.types";
import { AppInfo } from "../../types/app-info.types";
import { RootState } from "../../store";

const { Title, Text } = Typography;
const { TextArea } = Input;

export const AppInfoList: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [appInfos, setAppInfos] = useState<AppInfo[]>([]);
  const [searchText, setSearchText] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reviewComment, setReviewComment] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [reviewAction, setReviewAction] = useState<"approve" | "reject" | null>(
    null
  );
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { isMobileDevice } = useSelector((state: RootState) => state.ui);

  const canEditAppInfo = hasPermission(PERMISSIONS.UPDATE_APP_INFO.name);
  const canDeleteAppInfo = hasPermission(PERMISSIONS.DELETE_APP_INFO.name);

  // Filter data based on search term
  const getFilteredData = () => {
    if (!searchText) return appInfos;

    const searchLower = searchText.toLowerCase();
    return appInfos.filter((app) => {
      const searchFields = [
        app.appName,
        app.packageName,
        app.version,
        app.versionCode?.toString(),
        app.reviewStatus,
        app.createdBy,
      ].map((field) => field?.toLowerCase() || "");

      return searchFields.some((field) => field.includes(searchLower));
    });
  };

  const fetchAppInfos = async () => {
    try {
      setLoading(true);
      const response: ApiResponse<AppInfo[]> = await appInfoService.getAll();
      if (response.status === OperationStatus.SUCCESS && response.data) {
        setAppInfos(response.data);
      } else {
        message.error(response.message || "Failed to load app information");
      }
    } catch (error) {
      message.error("Failed to load app information");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppInfos();
  }, []);

  const handleEdit = (id: string) => {
    navigate(`${PERMISSIONS.ADD_APP_INFO.route}`, { state: { id } });
  };

  const handleDelete = async (id: string) => {
    Modal.confirm({
      title: "Delete App Info",
      content: "Are you sure you want to delete this app info?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          const response: ApiResponse<string> = await appInfoService.delete(id);
          if (response.status === OperationStatus.SUCCESS) {
            message.success("App info deleted successfully");
            fetchAppInfos();
          } else {
            message.error(response.message || "Failed to delete app info");
          }
        } catch (error) {
          message.error("Failed to delete app info");
        }
      },
    });
  };

  const handleReviewAction = (id: string, action: "approve" | "reject") => {
    setSelectedId(id);
    setReviewAction(action);
    setModalVisible(true);
  };

  const handleReviewSubmit = async () => {
    if (!selectedId || !reviewAction || !reviewComment.trim()) {
      message.warning("Please provide a review comment");
      return;
    }

    try {
      let response: ApiResponse<AppInfo>;
      if (reviewAction === "approve") {
        response = await appInfoService.approve(selectedId, reviewComment);
      } else {
        response = await appInfoService.reject(selectedId, reviewComment);
      }

      if (response.status === OperationStatus.SUCCESS) {
        message.success(`App info ${reviewAction}d successfully`);
        setModalVisible(false);
        setReviewComment("");
        fetchAppInfos();
      } else {
        message.error(response.message || `Failed to ${reviewAction} app info`);
      }
    } catch (error) {
      message.error(`Failed to ${reviewAction} app info`);
    }
  };

  const renderAppInfoItem = (app: AppInfo) => (
    <List.Item
      key={app.id}
      style={{
        padding: "16px",
        marginBottom: "8px",
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.03)",
      }}
    >
      <List.Item.Meta
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            <Space wrap>
              <Text
                strong
                style={{ wordBreak: "break-word", fontSize: "15px" }}
              >
                {app.appName}
              </Text>
              <Tag
                color={
                  app.reviewStatus === "APPROVED"
                    ? "success"
                    : app.reviewStatus === "REJECTED"
                    ? "error"
                    : "warning"
                }
                style={{ margin: 0 }}
              >
                {app.reviewStatus}
              </Tag>
            </Space>
            <Space>
              {canEditAppInfo && (
                <Tooltip title="Edit">
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(app.id);
                    }}
                  />
                </Tooltip>
              )}
              {canDeleteAppInfo && (
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(app.id);
                  }}
                />
              )}
              {app.reviewStatus === "PENDING" && (
                <>
                  <Tooltip title="Approve">
                    <Button
                      type="text"
                      size="small"
                      icon={<CheckOutlined />}
                      style={{ color: "#52c41a" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReviewAction(app.id, "approve");
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Reject">
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<CloseOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReviewAction(app.id, "reject");
                      }}
                    />
                  </Tooltip>
                </>
              )}
            </Space>
          </div>
        }
        description={
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
              <Text style={{ color: "#8c8c8c", fontSize: "13px" }}>
                Package:
              </Text>
              <Text style={{ wordBreak: "break-word" }}>{app.packageName}</Text>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
              <Text style={{ color: "#8c8c8c", fontSize: "13px" }}>
                Version:
              </Text>
              <Text>{app.version}</Text>
              {app.versionCode && (
                <>
                  <Text style={{ color: "#8c8c8c", fontSize: "13px" }}>
                    Code:
                  </Text>
                  <Text>{app.versionCode}</Text>
                </>
              )}
            </div>
            {app.downloadLink && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                <Text style={{ color: "#8c8c8c", fontSize: "13px" }}>
                  Download:
                </Text>
                <Text style={{ wordBreak: "break-all" }}>
                  {app.downloadLink}
                </Text>
              </div>
            )}
            {app.adminComponentName && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                <Text style={{ color: "#8c8c8c", fontSize: "13px" }}>
                  Admin Component:
                </Text>
                <Text style={{ wordBreak: "break-word" }}>
                  {app.adminComponentName}
                </Text>
              </div>
            )}
            {app.clientSignatureChecksum && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                <Text style={{ color: "#8c8c8c", fontSize: "13px" }}>
                  Signature:
                </Text>
                <Text style={{ wordBreak: "break-all" }}>
                  {app.clientSignatureChecksum}
                </Text>
              </div>
            )}
            {app.firebasePackageName && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                <Text style={{ color: "#8c8c8c", fontSize: "13px" }}>
                  Firebase Package:
                </Text>
                <Text style={{ wordBreak: "break-word" }}>
                  {app.firebasePackageName}
                </Text>
              </div>
            )}
          </Space>
        }
      />
    </List.Item>
  );

  return (
    <Card
      title="App Information"
      extra={
        <div style={{ display: "flex", gap: "8px", width: "100%" }}>
          <Input
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
            style={{ flex: "1 1 auto", minWidth: "120px" }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(PERMISSIONS.ADD_APP_INFO.route)}
          >
            Add New
          </Button>
        </div>
      }
    >
      <List
        itemLayout="horizontal"
        size="large"
        dataSource={getFilteredData()}
        loading={loading}
        pagination={{
          pageSize: 200,
          responsive: true,
          showSizeChanger: false,
        }}
        renderItem={renderAppInfoItem}
        style={{ marginTop: "16px" }}
      />

      <Modal
        title={`${reviewAction === "approve" ? "Approve" : "Reject"} App Info`}
        open={modalVisible}
        onOk={handleReviewSubmit}
        onCancel={() => setModalVisible(false)}
        okText={reviewAction === "approve" ? "Approve" : "Reject"}
        okButtonProps={{ type: "primary", danger: reviewAction === "reject" }}
      >
        <div style={{ marginBottom: "16px" }}>
          <div style={{ marginBottom: "8px", fontWeight: 500 }}>Comment</div>
          <TextArea
            rows={4}
            value={reviewComment}
            onChange={(e) => setReviewComment(e.target.value)}
            placeholder={`Enter your comment for ${reviewAction}...`}
          />
        </div>
      </Modal>
    </Card>
  );
};
